import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Share2, Trophy, Layers, Video } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface ClimbProfileFabProps {
  isAuthenticated: boolean;
  /** When true, the "+ Top" action is hidden (the user has already logged a send). */
  hasToppedClimb?: boolean;
  onAddTry: () => void;
  onAddTop: () => void;
  onShare: () => void;
  onAddVideo: () => void;
  /** When provided (collapsible board climbs), offers "add inclination variant". */
  onAddVariant?: () => void;
  /**
   * False on a climb that takes no attempts — a board problem archived with
   * its wall's old photo: "+ Try" and "+ Top" are not offered.
   */
  canLog?: boolean;
}

interface FabAction {
  label: string;
  icon: typeof Plus;
  onClick: () => void;
  variant: 'primary' | 'success' | 'secondary';
  /** When true, the popover stays open after the action is invoked. */
  keepOpen?: boolean;
}

const variantClasses: Record<FabAction['variant'], string> = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
  success: 'bg-green-600 text-white hover:bg-green-700',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
};

export function ClimbProfileFab({
  isAuthenticated,
  hasToppedClimb = false,
  onAddTry,
  onAddTop,
  onShare,
  onAddVideo,
  onAddVariant,
  canLog = true,
}: ClimbProfileFabProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const actions: FabAction[] = [
    ...(isAuthenticated && canLog
      ? [
          {
            // The count is picked in the sheet this opens, so the menu has no
            // reason to stay up waiting for a second tap.
            label: t('climbProfile.fab.addTry'),
            icon: Plus,
            onClick: onAddTry,
            variant: 'primary' as const,
          },
        ]
      : []),
    ...(isAuthenticated && onAddVariant
      ? [
          {
            label: t('climbProfile.fab.addVariant'),
            icon: Layers,
            onClick: onAddVariant,
            variant: 'secondary' as const,
          },
        ]
      : []),
    ...(isAuthenticated && canLog && !hasToppedClimb
      ? [
          {
            label: t('climbProfile.fab.addTop'),
            icon: Trophy,
            onClick: onAddTop,
            variant: 'success' as const,
          },
        ]
      : []),
    {
      label: t('climbProfile.fab.addVideo'),
      icon: Video,
      onClick: onAddVideo,
      variant: 'secondary' as const,
    },
    {
      label: t('climbProfile.fab.share'),
      icon: Share2,
      onClick: onShare,
      variant: 'secondary' as const,
    },
  ];

  const runAction = (action: FabAction) => {
    if (!action.keepOpen) {
      setOpen(false);
    }
    action.onClick();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label={t('climbProfile.fab.actionsLabel')}
          className={cn(
            'fixed z-50 rounded-full h-14 w-14 p-0 shadow-lg',
            'right-4 md:right-6',
            'bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:bottom-6',
            'data-[state=open]:rotate-45 transition-transform duration-200',
          )}
        >
          <Plus className="h-6 w-6" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={12}
        className="w-auto p-0 bg-transparent border-none shadow-none"
      >
        <div className="flex flex-col items-end gap-3">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.label}
                type="button"
                onClick={() => runAction(action)}
                className="flex items-center gap-2 group"
              >
                <span className="text-sm font-medium bg-background/95 text-foreground px-3 py-1.5 rounded-md shadow border">
                  {action.label}
                </span>
                <span
                  className={cn(
                    'inline-flex h-12 w-12 items-center justify-center rounded-full shadow-md transition-transform group-hover:scale-105',
                    variantClasses[action.variant],
                  )}
                >
                  <Icon className="h-5 w-5" />
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
